import { JSX, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Button } from '../../components/button';
import { MonthDaySelector, WeekDaySelector } from '../../components/day-selector';
import { Error } from '../../components/error';
import { Form } from '../../components/form';
import { TimeCount } from '../../components/time-count';
import { HabitCategory, HabitConfig } from '../../config/habit.config';
import {
  HabitFrequencyType,
  HabitFrequencyTypeLabels,
  HabitTargetType,
  SpecialDays,
  SpecialDaysLabels,
  TargetDirection,
  TargetDirectionLabels,
  TargetEvaluation,
  TargetEvaluationLabels,
  WeekDay,
} from '../../dto/habit.enum';
import { useFirebase } from '../../hooks/firebase.hook';
import { useHabit } from '../../hooks/habit.hook';
import { useLayout } from '../../hooks/layout.hook';
import { useNavigation } from '../../hooks/navigation.hook';

interface FormData {
  title: string;
  notes?: string;
  frequency?: HabitFrequencyType;
  target?: string;
  evaluation?: TargetEvaluation;
  direction?: TargetDirection;
  specialDays?: SpecialDays;
}

export default function HabitNewScreen(): JSX.Element {
  const { t } = useTranslation();
  const { navigate } = useNavigation();
  const { translateError } = useFirebase();
  const { startHabit } = useHabit();
  const location = useLocation();

  const habit = location.state as HabitConfig;
  const isCustomHabit = habit.category === HabitCategory.OWN_HABIT;

  const [frequency, setFrequency] = useState<HabitFrequencyType>(habit.frequencyType);
  const [weekDays, setWeekDays] = useState<WeekDay[]>([]);
  const [monthDays, setMonthDays] = useState<number[]>([]);
  const [count, setCount] = useState<number>(habit.frequencyConfig?.count ?? 1);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [targetEvaluation, setTargetEvaluation] = useState<TargetEvaluation>();
  const [targetDirection, setTargetDirection] = useState<TargetDirection>();

  const maxCount = frequency === HabitFrequencyType.WEEKLY ? 6 : 20;

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!habit) navigate(-1);
  }, [habit]);

  useEffect(() => {
    if (count > maxCount) setCount(maxCount);
    if (count < 0) setCount(0);
  }, [count, frequency]);

  function handleSubmit({ title, notes, frequency, target, evaluation, direction, specialDays }: FormData) {
    setIsSaving(true);

    startHabit({
      ...habit,
      title,
      notes,
      targetConfig: {
        value: target != null ? +target : undefined,
        evaluation: evaluation ?? habit.targetConfig?.evaluation ?? TargetEvaluation.DAILY,
        direction: direction ?? habit.targetConfig?.direction ?? TargetDirection.GROWTH,
      },
      frequencyType: frequency ?? habit.frequencyType,
      frequencyConfig: { count, weekDays, monthDays, specialDays },
    })
      .then(() => navigate(-3))
      .catch((e) => setError(translateError(e.message)))
      .finally(() => setIsSaving(false));
  }

  useLayout(() => ({ activeMenu: 'habits', title: t(habit.title), titleSize: 'text-xl', backButton: true }), [t]);

  return (
    <>
      {habit.description && !isCustomHabit && (
        <>
          <div className="text-sm text-base-content/70 whitespace-pre-line hyphens-auto">{t(habit.description)}</div>
          <div className="divider my-1" />
        </>
      )}

      <Form onSubmit={handleSubmit}>
        <div className="flex flex-col gap-4">
          <div>
            <div className="text-base-content/70 mb-1">{t('How would you like to call your new habit?')}</div>
            <input
              defaultValue={isCustomHabit ? undefined : t(habit.title)}
              name="title"
              className="input input-lg validator w-full"
              placeholder={t('Label')}
              required
            />
            <div className="validator-hint hidden">{t('Required')}</div>
          </div>

          <div>
            <div className="text-base-content/70 mb-1">{t('What is your exact goal you want to achieve?')}</div>
            <textarea className="textarea textarea-lg w-full" placeholder={t('Personal goal')} name="notes" />
          </div>

          {habit.targetType === HabitTargetType.QUANTITY && (
            <>
              <div>
                <div className="text-base-content/70 mb-1">{t('How many or how much do you want to achieve?')}</div>
                <input
                  type="number"
                  name="target"
                  className="input input-lg validator w-full"
                  placeholder={t(habit.targetConfig?.valueHint ?? 'Target')}
                  required
                />
                <div className="validator-hint hidden">{t('Required')}</div>
              </div>

              {isCustomHabit && (
                <div>
                  <div className="text-base-content/70 mb-1">{t('What kind of target is it?')}</div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <select
                      className="select select-lg w-full"
                      name="evaluation"
                      value={targetEvaluation}
                      onChange={(e) => setTargetEvaluation(e.target.value as TargetEvaluation)}
                    >
                      {Object.values(TargetEvaluation).map((te) => (
                        <option key={te} value={te}>
                          {t(TargetEvaluationLabels[te])}
                        </option>
                      ))}
                    </select>

                    <select
                      className="select select-lg w-full"
                      name="direction"
                      value={targetDirection}
                      onChange={(e) => setTargetDirection(e.target.value as TargetDirection)}
                    >
                      {Object.values(TargetDirection).map((td) => (
                        <option key={td} value={td}>
                          {t(TargetDirectionLabels[td])}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </>
          )}

          <div>
            <div className="text-base-content/70 mb-1">{t('How often?')}</div>
            <select
              className="select select-lg w-full"
              name="frequency"
              value={frequency}
              onChange={(e) => setFrequency(e.target.value as HabitFrequencyType)}
            >
              {Object.values(HabitFrequencyType).map((ft) => (
                <option key={ft} value={ft}>
                  {t(HabitFrequencyTypeLabels[ft])}
                </option>
              ))}
            </select>

            {frequency && (
              <div className="mt-3 mb-4">
                {[HabitFrequencyType.WEEKLY, HabitFrequencyType.MONTHLY].includes(frequency) && (
                  <>
                    <div className="text-base-content/70 mb-1">
                      <TimeCount count={count} frequency={frequency} />
                    </div>

                    <input
                      name="count"
                      value={count}
                      type="range"
                      min={1}
                      max={maxCount}
                      onChange={(e) => setCount(+e.target.value)}
                      className="range range-primary w-full"
                      step={1}
                    />

                    <div className="flex justify-between px-1 my-2 text-xs w-full">
                      {Array.from({ length: maxCount }).map((_, index) => (
                        <div key={index} className="w-[16px] text-center">
                          {index + 1}
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {frequency === HabitFrequencyType.WEEK_DAYS && (
                  <>
                    <WeekDaySelector selectedDays={weekDays} setSelectedDays={setWeekDays} />
                  </>
                )}

                {frequency === HabitFrequencyType.MONTH_DAYS && (
                  <>
                    <MonthDaySelector selectedDays={monthDays} setSelectedDays={setMonthDays} />
                  </>
                )}

                {frequency === HabitFrequencyType.SPECIAL_DAYS && (
                  <>
                    <select
                      className="select select-lg w-full"
                      name="specialDays"
                      defaultValue={habit.frequencyConfig?.specialDays}
                    >
                      {Object.values(SpecialDays).map((sd) => (
                        <option key={sd} value={sd}>
                          {t(SpecialDaysLabels[sd])}
                        </option>
                      ))}
                    </select>
                  </>
                )}
              </div>
            )}
          </div>

          <Button className="btn btn-primary btn-lg" type="submit" loading={isSaving}>
            {t('Start')}
          </Button>

          {error && <Error msg={error} />}
        </div>
      </Form>
    </>
  );
}
